import { numberParam, stringParam } from '../types/checks.js';
import type { CheckResult } from '../types/checks.js';
import type { NormalizedGeometry } from '../types/geometry.js';
import { clampNormalizedGeometry, roundTo } from '../types/geometry.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import type { DocumentProfile } from '../types/profile.js';
import type { VisualMeasurement } from '../types/result.js';
import { FACE_OVAL } from '../geometry/landmarks.js';

/**
 * Static guides: the head zone, the eye line, the centre line.
 *
 * Guides are produced by the core — not by the overlay — and they are derived
 * from the *same* rule values the checks use. That is what guarantees the user
 * never sees a green zone that disagrees with the verdict, and it is why the
 * overlay contains no thresholds of its own.
 *
 * Guides are emitted even when no face was found, so the live camera mode can
 * show the target framing before anything is detected. In that case they are
 * marked `guide: true` and their status is `not_checked`.
 */

interface GuideInput {
  profile: DocumentProfile;
  geometry?: FaceGeometryMetrics | undefined;
  /** Status of the corresponding check, when it ran. */
  statusOf: (checkId: string) => CheckResult | undefined;
}

const rectangle = (
  x: number,
  y: number,
  width: number,
  height: number,
): NormalizedGeometry => ({
  type: 'rect',
  coordinates: [roundTo(x, 4), roundTo(y, 4), roundTo(width, 4), roundTo(height, 4)],
});

const horizontalLine = (y: number): NormalizedGeometry => ({
  type: 'line',
  coordinates: [0, roundTo(y, 4), 1, roundTo(y, 4)],
});

export const buildGuides = (input: GuideInput): VisualMeasurement[] => {
  const { profile, geometry } = input;
  const visual = profile.visual;
  const measurements: VisualMeasurement[] = [];

  const facePosition = input.statusOf('face_position');
  const eyeLine = input.statusOf('eye_line');
  const headSize = input.statusOf('head_size');
  const faceCount = input.statusOf('face_count');

  // ---------------------------------------------------------------- centre
  if (visual.showCenterLine) {
    const tolerance = numberParam(
      (profile.rules.face_position?.params ?? {}) as Record<string, unknown>,
      'maxHorizontalOffset',
      0.05,
    );
    measurements.push({
      id: 'guide.center_line',
      label: visual.labels?.centerLine ?? 'Центр кадра',
      status: facePosition?.status ?? 'not_checked',
      severity: facePosition?.severity ?? 'info',
      geometry: [
        rectangle(roundTo(0.5 - tolerance, 4), 0, roundTo(tolerance * 2, 4), 1),
        { type: 'line', coordinates: [0.5, 0, 0.5, 1] },
      ],
      message:
        geometry !== undefined
          ? facePosition?.message ?? 'Положение головы относительно центра кадра.'
          : 'Вертикальная линия центра кадра: голова должна быть симметрична относительно неё.',
      fixability: 'none',
      guide: true,
      interactive: true,
      displayValue: `${(tolerance * 100).toFixed(0)} %`,
    });
  }

  // -------------------------------------------------------------- eye line
  if (visual.showEyeLine) {
    const params = (profile.rules.eye_line?.params ?? {}) as Record<string, unknown>;
    const min = numberParam(params, 'min', 0.56);
    const max = numberParam(params, 'max', 0.69);
    const reference = stringParam(params, 'reference', 'bottom');
    const bandTop = reference === 'bottom' ? 1 - max : min;
    const bandBottom = reference === 'bottom' ? 1 - min : max;

    const geometries: NormalizedGeometry[] = [
      rectangle(0, roundTo(bandTop, 4), 1, roundTo(bandBottom - bandTop, 4)),
    ];
    if (geometry) geometries.push(horizontalLine(geometry.eyeLineFromTop));

    measurements.push({
      id: 'guide.eye_band',
      label: 'Зона положения глаз',
      status: eyeLine?.status ?? 'not_checked',
      severity: eyeLine?.severity ?? 'info',
      geometry: geometries,
      min,
      max,
      unit: 'ratio',
      message:
        eyeLine?.message ??
        `Допустимая зона для линии глаз: ${(min * 100).toFixed(0)}–${(max * 100).toFixed(0)} % высоты изображения.`,
      fixability: 'none',
      guide: true,
      interactive: true,
      displayValue:
        geometry !== undefined
          ? `${(geometry.eyeLineFromBottom * 100).toFixed(1).replace('.', ',')} %`
          : `${(min * 100).toFixed(0)}–${(max * 100).toFixed(0)} %`,
    });
  }

  // ------------------------------------------------------------- head zone
  if (visual.showHeadZone) {
    const params = (profile.rules.head_size?.params ?? {}) as Record<string, unknown>;
    const min = numberParam(params, 'min', 0.5);
    const max = numberParam(params, 'max', 0.69);

    const anchored = geometry !== undefined && geometry.headTopSource !== 'unavailable';
    let chinY: number;
    let targetHead: number;
    if (anchored && geometry) {
      chinY = geometry.chinPoint.y;
      targetHead = geometry.headHeight;
    } else {
      const eyeParams = (profile.rules.eye_line?.params ?? {}) as Record<string, unknown>;
      const eyeMin = numberParam(eyeParams, 'min', 0.56);
      const eyeMax = numberParam(eyeParams, 'max', 0.69);
      const eyeReference = stringParam(eyeParams, 'reference', 'bottom');
      const targetEyeFromBottom = (eyeMin + eyeMax) / 2;
      const targetEyeFromTop = eyeReference === 'bottom' ? 1 - targetEyeFromBottom : targetEyeFromBottom;
      targetHead = (min + max) / 2;
      // Eye line sits at roughly the middle of the head, so the chin is half a
      // head below it — the same assumption the head-top estimator documents.
      chinY = targetEyeFromTop + targetHead * 0.5;
    }

    // Allowed positions of the *top of the head* for the measured (or assumed) chin.
    const topLimitMin = chinY - max;
    const topLimitMax = chinY - min;

    measurements.push({
      id: 'guide.head_zone',
      label: 'Допустимая зона головы',
      status: headSize?.status ?? 'not_checked',
      severity: headSize?.severity ?? 'info',
      geometry: [
        rectangle(0, roundTo(topLimitMin, 4), 1, roundTo(Math.max(0.001, topLimitMax - topLimitMin), 4)),
        horizontalLine(topLimitMin),
        horizontalLine(topLimitMax),
      ],
      min,
      max,
      unit: 'ratio',
      message:
        headSize?.message ??
        `Допустимая высота головы: ${(min * 100).toFixed(0)}–${(max * 100).toFixed(0)} % высоты кадра.`,
      fixability: 'none',
      guide: true,
      interactive: true,
      displayValue:
        anchored && geometry
          ? `${(geometry.headHeightRatio * 100).toFixed(1).replace('.', ',')} %`
          : `${(min * 100).toFixed(0)}–${(max * 100).toFixed(0)} %`,
    });

    if (anchored && geometry) {
      measurements.push({
        id: 'guide.head_measure',
        label: 'Голова: макушка → подбородок',
        status: headSize?.status ?? 'not_checked',
        severity: headSize?.severity ?? 'info',
        geometry: [
          {
            type: 'line',
            coordinates: [
              roundTo(geometry.faceCenterX, 4),
              roundTo(geometry.headTopPoint.y, 4),
              roundTo(geometry.faceCenterX, 4),
              roundTo(geometry.chinPoint.y, 4),
            ],
          },
          { type: 'point', coordinates: [roundTo(geometry.faceCenterX, 4), roundTo(geometry.headTopPoint.y, 4)] },
          { type: 'point', coordinates: [roundTo(geometry.faceCenterX, 4), roundTo(geometry.chinPoint.y, 4)] },
        ],
        value: roundTo(geometry.headHeightRatio, 4),
        min,
        max,
        unit: 'ratio',
        message: `Высота головы: ${(geometry.headHeightRatio * 100).toFixed(1).replace('.', ',')} % высоты кадра.`,
        fixability: 'none',
        guide: true,
        interactive: true,
        displayValue: `${(geometry.headHeightRatio * 100).toFixed(1).replace('.', ',')} %`,
      });
    }
  }

  // --------------------------------------------------------- face outline
  if (geometry?.landmarks && geometry.landmarks.length > 0) {
    const points: number[] = [];
    for (const index of FACE_OVAL) {
      const landmark = geometry.landmarks[index];
      if (!landmark) continue;
      points.push(roundTo(landmark.x, 4), roundTo(landmark.y, 4));
    }
    if (points.length >= 6) {
      // The oval outline is informational: it shows what the analysis sees.
      const acknowledged = faceCount !== undefined && faceCount.status === 'pass';
      measurements.push({
        id: 'guide.face_outline',
        label: 'Контур лица',
        status: acknowledged ? 'pass' : (faceCount?.status ?? 'not_checked'),
        severity: 'info',
        geometry: [{ type: 'polygon', coordinates: points }],
        message: 'Контур лица, определённый автоматически.',
        fixability: 'none',
        guide: true,
        interactive: false,
      });
    }
  }

  return measurements.map((measurement) => ({
    ...measurement,
    geometry: measurement.geometry.map(clampNormalizedGeometry),
  }));
};
